import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { useGetAPI } from '../Utils/useGetAPI';

export interface GroupMemberships {
  squad: string;
  department: string;
  business_unit: string;
  default: boolean;
}

export interface User {
  email: string;
  username: string;
  groupMemberships: GroupMemberships[];
  lastSeenBanner?: string;
}

export interface UserFetchObj {
  email: string;
  group_memberships: GroupMemberships[];
  username: string;
  lastSeenBanner?: string;
}

export interface UserStorage {
  user: User;
  expiry: number;
}

export type UserContextType = {
  getUser: () => Promise<User>;
  updateUser: (fetchedUser: UserFetchObj) => User;
};

const userContextStorageKey = 'devportal-userContext';

const getInitialState = () => {
  const user = localStorage.getItem(userContextStorageKey);
  return user ? JSON.parse(user) : null;
};

const userContext = createContext<UserContextType | null>(null);

const UserContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const now = new Date();
  const ttl = 86400000; // 1 day in ms
  const [user, setUser] = useState<UserStorage>(getInitialState);
  const identityAPI = useApi(microsoftAuthApiRef);
  const url = useGetAPI();

  const saveUser = (fetchedUser: UserFetchObj) => {
    const defaultGroupMembershipArr = fetchedUser.group_memberships.map(
      membership => {
        if (membership.squad === fetchedUser.group_memberships[0].squad) {
          return {
            squad: membership.squad,
            business_unit: membership.business_unit,
            department: membership.department,
            default: true,
          };
        }

        return {
          squad: membership.squad,
          business_unit: membership.business_unit,
          department: membership.department,
          default: false,
        };
      },
    );

    const newUser: User = {
      email: fetchedUser.email,
      username: fetchedUser.username,
      groupMemberships: defaultGroupMembershipArr,
    };

    setUser({
      user: newUser,
      expiry: now.getTime() + ttl,
    });

    return newUser;
  };

  const retrieveUserContext = async () => {
    const token = await identityAPI.getIdToken();
    const { data } = await axios.get<UserFetchObj>(
      `${url}/v1/api/userinfo`,

      {
        headers: {
          Authorization: `Basic ${token}`,
        },
      },
    );

    return saveUser(data);
  };

  useEffect(() => {
    localStorage.setItem(userContextStorageKey, JSON.stringify(user));
  }, [user]);

  useEffect(() => {
    (async () => {
      const token = await identityAPI.getIdToken();
      const chatBotObj = {
        bearersToken: token,
        url: 'https://devportal-api.dev.acuitynext.io/v1/api/chat/sendPrompt',
      };
      localStorage.setItem('chatBotObj', JSON.stringify(chatBotObj));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      return !getInitialState() && (await retrieveUserContext());
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getUser = async () => {
    if (!user || !user.expiry || now.getTime() > user.expiry) {
      localStorage.removeItem(userContextStorageKey);
      return await retrieveUserContext();
    }

    return user.user;
  };

  const updateUser = (fetchedUser: UserFetchObj) => {
    const newUser: User = {
      email: fetchedUser.email,
      username: fetchedUser.username,
      groupMemberships: fetchedUser.group_memberships,
    };

    setUser({
      user: newUser,
      expiry: now.getTime() + ttl,
    });

    return newUser;
  };

  return (
    <userContext.Provider value={{ getUser, updateUser }}>
      {children}
    </userContext.Provider>
  );
};

export const useUser = () => useContext(userContext) as UserContextType;

export default UserContextProvider;
